import { useState, useEffect } from "react";
import { base44 } from "../../api/base44Client";
import {
  Button,
  TextField,
  Box,
  Typography,
  Paper,
  IconButton,
  MenuItem,
  Select,
  FormControl,
  CircularProgress,
  Grid,
  Alert,
  Chip,
} from "@mui/material";
import {
  Link2,
  Send,
  Copy,
  RefreshCw,
  Ban,
  Mail,
  MessageSquare,
  CheckCircle2,
  Clock,
  AlertCircle,
  XCircle,
  ExternalLink,
  Eye,
} from "lucide-react";
import moment from "moment";

const STATUS_CONFIG = {
  generated: { label: "Generato", icon: Link2, color: "default" },
  sent: { label: "Inviato", icon: Send, color: "primary" },
  opened: { label: "Aperto", icon: Eye, color: "info" },
  in_progress: { label: "In compilazione", icon: Clock, color: "warning" },
  submitted: {
    label: "Inviato dal cliente",
    icon: CheckCircle2,
    color: "success",
  },
  expired: { label: "Scaduto", icon: AlertCircle, color: "warning" },
  revoked: { label: "Revocato", icon: XCircle, color: "error" },
};

export default function CustomerLinkManager({
  financingRequestId,
  praticaId,
  customerEmail,
  customerName,
}) {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [sending, setSending] = useState(false);
  const [copied, setCopied] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [expirationDays, setExpirationDays] = useState("7");
  const [sendEmail, setSendEmail] = useState(customerEmail || "");

  const activeLink = links.find(
    (l) => !["revoked", "expired"].includes(l.status),
  );

  useEffect(() => {
    loadLinks();
  }, [financingRequestId]);

  async function loadLinks() {
    setLoading(true);
    const data = await base44.entities.CustomerLink.filter({
      financing_request_id: financingRequestId,
    });
    data.sort((a, b) => new Date(b.generated_at) - new Date(a.generated_at));
    setLinks(data);
    setLoading(false);
  }

  async function handleGenerate() {
    setGenerating(true);
    await base44.functions.invoke("generateCustomerLink", {
      financing_request_id: financingRequestId,
      expiration_days: parseInt(expirationDays),
      customer_email: sendEmail,
      customer_name: customerName,
    });
    await loadLinks();
    setGenerating(false);
  }

  async function handleSendEmail() {
    if (!activeLink) return;
    setSending(true);
    setSendResult(null);
    const res = await base44.functions.invoke("sendCustomerLink", {
      link_id: activeLink.id,
      method: "email",
    });
    await loadLinks();
    setSending(false);
    setSendResult(res.data);
  }

  async function handleRevoke() {
    if (!activeLink) return;
    await base44.functions.invoke("revokeCustomerLink", {
      link_id: activeLink.id,
    });
    await loadLinks();
  }

  function buildIntakeUrl(token) {
    return `${window.location.origin}/intake/${token}`;
  }

  async function handleCopy() {
    if (!activeLink) return;
    await navigator.clipboard.writeText(buildIntakeUrl(activeLink.token));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  function whatsappText(link) {
    const name = (link.customer_name || "Cliente").split(" ")[0];
    const url = buildIntakeUrl(link.token);
    return `Ciao ${name}, ti inviamo il link per completare la richiesta collegata alla tua polizza. Abbiamo già inserito parte dei dati. Ti basterà verificare e completare quelli mancanti: ${url}`;
  }

  if (loading)
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress size={24} color="inherit" />
      </Box>
    );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Link2 size={18} style={{ color: "#2196f3" }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          Link Cliente
        </Typography>
      </Box>

      {!activeLink ? (
        <Paper
          variant="outlined"
          sx={{ p: 2, borderRadius: 3, bgcolor: "action.hover" }}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mb: 2 }}
          >
            Nessun link attivo. Genera un link sicuro da inviare al cliente.
          </Typography>
          <Grid container spacing={1.5} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                size="small"
                placeholder="Email cliente"
                value={sendEmail}
                onChange={(e) => setSendEmail(e.target.value)}
                sx={{ "& .MuiInputBase-input": { fontSize: "0.75rem" } }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <Select
                  value={expirationDays}
                  onChange={(e) => setExpirationDays(e.target.value)}
                  sx={{ fontSize: "0.75rem" }}
                >
                  <MenuItem value="3" sx={{ fontSize: "0.75rem" }}>
                    3 giorni
                  </MenuItem>
                  <MenuItem value="7" sx={{ fontSize: "0.75rem" }}>
                    7 giorni
                  </MenuItem>
                  <MenuItem value="14" sx={{ fontSize: "0.75rem" }}>
                    14 giorni
                  </MenuItem>
                  <MenuItem value="30" sx={{ fontSize: "0.75rem" }}>
                    30 giorni
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          <Button
            fullWidth
            variant="contained"
            size="small"
            onClick={handleGenerate}
            disabled={generating}
            startIcon={
              generating ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <Link2 size={16} />
              )
            }
            sx={{ textTransform: "none", borderRadius: 2 }}
          >
            Genera link sicuro
          </Button>
        </Paper>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {/* Status card */}
          {(() => {
            const cfg =
              STATUS_CONFIG[activeLink.status] || STATUS_CONFIG.generated;
            const Icon = cfg.icon;
            return (
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 2,
                  }}
                >
                  <Chip
                    label={cfg.label}
                    color={cfg.color}
                    size="small"
                    icon={<Icon size={14} />}
                    sx={{ fontWeight: 600 }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    Scade: {moment(activeLink.expires_at).format("DD/MM/YY")}
                  </Typography>
                </Box>

                <Paper
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    mb: 2,
                    bgcolor: "action.hover",
                    borderRadius: 2,
                    wordBreak: "break-all",
                    userSelect: "all",
                    fontFamily: "monospace",
                    fontSize: "0.625rem",
                    color: "text.secondary",
                  }}
                >
                  {buildIntakeUrl(activeLink.token)}
                </Paper>

                {/* Metrics */}
                <Grid container spacing={1} sx={{ mb: 2 }}>
                  {[
                    { label: "Aperture", value: activeLink.open_count || 0 },
                    {
                      label: "Completamento",
                      value: `${activeLink.completion_percentage || 0}%`,
                    },
                    {
                      label: "Prima apertura",
                      value: activeLink.first_opened_at
                        ? moment(activeLink.first_opened_at).format("DD/MM")
                        : "—",
                    },
                  ].map((m) => (
                    <Grid item xs={4} key={m.label}>
                      <Box
                        sx={{
                          bgcolor: "action.hover",
                          borderRadius: 2,
                          p: 1,
                          textAlign: "center",
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{ fontWeight: 700, display: "block" }}
                        >
                          {m.value}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ fontSize: "0.55rem", color: "text.secondary" }}
                        >
                          {m.label}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>

                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.5,
                    mb: 2,
                  }}
                >
                  {activeLink.generated_at && (
                    <Typography
                      variant="caption"
                      sx={{ fontSize: "0.625rem", color: "text.secondary" }}
                    >
                      🔗 Generato:{" "}
                      {moment(activeLink.generated_at).format("DD/MM/YY HH:mm")}
                    </Typography>
                  )}
                  {activeLink.sent_at && (
                    <Typography
                      variant="caption"
                      sx={{ fontSize: "0.625rem", color: "text.secondary" }}
                    >
                      📤 Inviato:{" "}
                      {moment(activeLink.sent_at).format("DD/MM/YY HH:mm")}
                    </Typography>
                  )}
                  {activeLink.first_opened_at && (
                    <Typography
                      variant="caption"
                      sx={{ fontSize: "0.625rem", color: "text.secondary" }}
                    >
                      👁 Aperto:{" "}
                      {moment(activeLink.first_opened_at).format(
                        "DD/MM/YY HH:mm",
                      )}
                    </Typography>
                  )}
                  {activeLink.last_activity_at && (
                    <Typography
                      variant="caption"
                      sx={{ fontSize: "0.625rem", color: "text.secondary" }}
                    >
                      ⏱ Ultima attività:{" "}
                      {moment(activeLink.last_activity_at).format(
                        "DD/MM/YY HH:mm",
                      )}
                    </Typography>
                  )}
                  {activeLink.submitted_at && (
                    <Typography
                      variant="caption"
                      sx={{ fontSize: "0.625rem", color: "text.secondary" }}
                    >
                      ✅ Inviato dal cliente:{" "}
                      {moment(activeLink.submitted_at).format("DD/MM/YY HH:mm")}
                    </Typography>
                  )}
                </Box>

                {sendResult && !sendResult.email_sent && (
                  <Alert
                    severity="warning"
                    variant="outlined"
                    sx={{ mb: 2, borderRadius: 2, py: 0 }}
                  >
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: 600, display: "block" }}
                    >
                      Email non inviata automaticamente
                    </Typography>
                    <Typography variant="caption">
                      {sendResult.message}
                    </Typography>
                  </Alert>
                )}
                {sendResult && sendResult.email_sent && (
                  <Alert
                    severity="success"
                    variant="outlined"
                    sx={{ mb: 2, borderRadius: 2, py: 0 }}
                  >
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                      Email inviata con successo
                    </Typography>
                  </Alert>
                )}

                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <Button
                      fullWidth
                      size="small"
                      variant="outlined"
                      onClick={handleCopy}
                      startIcon={
                        copied ? (
                          <CheckCircle2 size={14} color="success" />
                        ) : (
                          <Copy size={14} />
                        )
                      }
                      sx={{
                        textTransform: "none",
                        px: 1,
                        borderRadius: 2,
                        fontSize: "0.7rem",
                      }}
                    >
                      {copied ? "Copiato!" : "Copia link"}
                    </Button>
                  </Grid>
                  <Grid item xs={6}>
                    <Button
                      fullWidth
                      size="small"
                      variant="outlined"
                      component="a"
                      href={buildIntakeUrl(activeLink.token)}
                      target="_blank"
                      startIcon={<ExternalLink size={14} />}
                      sx={{
                        textTransform: "none",
                        px: 1,
                        borderRadius: 2,
                        fontSize: "0.7rem",
                      }}
                    >
                      Anteprima
                    </Button>
                  </Grid>
                  <Grid item xs={6}>
                    <Button
                      fullWidth
                      size="small"
                      variant="outlined"
                      onClick={handleSendEmail}
                      disabled={sending || !sendEmail}
                      startIcon={
                        sending ? (
                          <CircularProgress size={14} color="inherit" />
                        ) : (
                          <Mail size={14} />
                        )
                      }
                      sx={{
                        textTransform: "none",
                        px: 1,
                        borderRadius: 2,
                        fontSize: "0.7rem",
                      }}
                    >
                      Invia email
                    </Button>
                  </Grid>
                  <Grid item xs={6}>
                    <Button
                      fullWidth
                      size="small"
                      variant="outlined"
                      component="a"
                      href={`https://wa.me/?text=${encodeURIComponent(
                        whatsappText(activeLink),
                      )}`}
                      target="_blank"
                      startIcon={<MessageSquare size={14} />}
                      sx={{
                        textTransform: "none",
                        px: 1,
                        borderRadius: 2,
                        fontSize: "0.7rem",
                      }}
                    >
                      WhatsApp
                    </Button>
                  </Grid>
                </Grid>
              </Paper>
            );
          })()}

          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={handleGenerate}
              disabled={generating}
              startIcon={
                generating ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  <RefreshCw size={14} />
                )
              }
              sx={{
                flex: 1,
                textTransform: "none",
                borderRadius: 2,
                fontSize: "0.75rem",
              }}
            >
              Rigenera
            </Button>
            <Button
              variant="outlined"
              size="small"
              color="error"
              onClick={handleRevoke}
              startIcon={<Ban size={14} />}
              sx={{
                textTransform: "none",
                borderRadius: 2,
                fontSize: "0.75rem",
              }}
            >
              Revoca
            </Button>
          </Box>

          {sendEmail && (
            <TextField
              fullWidth
              size="small"
              placeholder="Email cliente"
              value={sendEmail}
              onChange={(e) => setSendEmail(e.target.value)}
              sx={{ "& .MuiInputBase-input": { fontSize: "0.75rem" } }}
            />
          )}
        </Box>
      )}
    </Box>
  );
}
